import { useState } from 'react';

import styled from '@emotion/styled';

import {
  CountryCode,
  DisplayLanguage
} from 'src/utils/constants/stringConstants';

import { useAppStore } from 'src/hooks/useAppStore';
import { mixpanelObject } from '../../../../utils/analytics';
import {
  MIXPANEL_BUTTON_TYPE,
  MIXPANEL_ELEMENT,
  MIXPANEL_FOOTER
} from '../../../../utils/analytics/mixpanel/mixpanelConstants';
import {
  ctaEvent,
  timeEvent
} from '../../../../utils/analytics/mixpanel/mixpanelEventsHelper';
import { clearCtaEventsProperty } from '../../../../utils/analytics/mixpanel/mixpanelHelper';
import { getCookie } from '../../../../utils/browserStorageHandler';
import {
  COUNTRY,
  DISPLAY_LANGUAGE_COOKIE
} from '../../../../utils/constants/cookieConstants';
import * as style from './MenuColumnStyle';

const Container = styled.div`
  ${style.container};
`;
const Title = styled.div`
  ${style.title};
`;
const Collapsible = styled.div`
  ${style.slideDown};
  ${style.collapsible};
  overflow: hidden;
`;
const Item = styled.div`
  ${style.item};
`;

type MenuColumnProps = {
  title: string;
  slug?: string;
  items: {
    title: string;
    slug: string;
    external?: boolean;
  }[];
};

const trackMixPanelEvents = (element: string) => {
  timeEvent();
  clearCtaEventsProperty(mixpanelObject);
  mixpanelObject.setProperty(MIXPANEL_ELEMENT, element);
  mixpanelObject.setProperty(MIXPANEL_BUTTON_TYPE, MIXPANEL_FOOTER);
  ctaEvent();
};

const appendCountryAndLangInUrl = (
  url: string,
  countryCode: string,
  disLang: string
) => {
  if (
    countryCode &&
    disLang &&
    countryCode === CountryCode.IN &&
    disLang !== DisplayLanguage.ENGLISH
  ) {
    url = `/${disLang}${url}`;
  } else if (countryCode !== CountryCode.IN) {
    if (disLang && disLang !== DisplayLanguage.ENGLISH) {
      url = `/${disLang}/global${url}`;
    } else {
      url = `/global${url}`;
    }
  }
  return url;
};

const footerURLHelper = (
  slug: string,
  countryCode: string,
  isExternal: boolean,
  isTruncate = false
) => {
  if (isExternal) return slug;
  if (!isTruncate) {
    const displayLangCookie =
      getCookie(DISPLAY_LANGUAGE_COOKIE) || DisplayLanguage.ENGLISH;
    const countryCookie = getCookie(COUNTRY) || countryCode || CountryCode.IN;
    return appendCountryAndLangInUrl(slug, countryCookie, displayLangCookie);
  }
  return slug;
};

const navigate = (
  link: string,
  element: string,
  countryCode: string,
  event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  external = false
) => {
  if (event) event.preventDefault();

  if (link && link !== location.pathname) {
    trackMixPanelEvents(element);

    //redirect
    if (external) {
      window.open(link, '_blank');
    } else {
      window.location.href = `${footerURLHelper(link, countryCode, external)}`;
    }
  }
};

const MenuColumn = ({ title, slug, items }: MenuColumnProps) => {
  const [showCollapsibleColumn, setShowCollapsibleColumn] = useState(false);

  const { usersLocationData: userLocationDataServerSide, usersLocationData } =
    useAppStore();

  const countryCode =
    usersLocationData?.country_code ||
    userLocationDataServerSide?.country_code ||
    '';

  const handleClick = () => {
    setShowCollapsibleColumn((previousState) => !previousState);
  };

  return (
    <Container>
      <Title onClick={handleClick}>
        {slug ? (
          <a href={footerURLHelper(slug, countryCode, false)}>{title}</a>
        ) : (
          <span>{title}</span>
        )}
        <span
          css={[
            style.showMoreContent,
            style.rotateArrow(showCollapsibleColumn)
          ]}></span>
      </Title>
      <Collapsible css={style.containerMobile(showCollapsibleColumn)}>
        {items?.map(({ title, slug, external }) => {
          return (
            <Item key={title}>
              <a
                css={style.dots}
                href={footerURLHelper(slug, countryCode, false)}
                onClick={(event) => {
                  navigate(slug, title, countryCode, event, external);
                }}
                title={title}>
                {title}
              </a>
            </Item>
          );
        })}
      </Collapsible>
    </Container>
  );
};

export default MenuColumn;
